import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import HmiView, { BoxLabel, HmiCrop, Outline } from './HmiView';
import { STATE_IDS, TARGET, TARGETS, pad, scoreFor } from './screenData';
import { fmt } from './translations';
import { BLUE, EASE, Intro, PURPLE, RED, Reveal, Segmented } from './ui';
import styles from './ScreenInspector.module.css';

const COLOR = { component: BLUE, item: PURPLE };
const FILL = { component: 'rgba(56,189,248,0.07)', item: 'rgba(192,132,252,0.09)' };
const FILL_ON = { component: 'rgba(56,189,248,0.22)', item: 'rgba(192,132,252,0.24)' };
const N = TARGETS.length;
const SCAN_MS = 900;
const DEFAULT_FOCUS = 'overview.pump_auto_mode';

/** Counts 0 → N, one entry every `ms`, restarting whenever `run` changes. */
function useReveal(run, ms, delay = 500) {
    const [shown, setShown] = useState(0);
    useEffect(() => {
        setShown(0);
        let n = 0;
        let id;
        const start = setTimeout(() => {
            id = setInterval(() => {
                n += 1;
                setShown(n);
                if (n >= N) clearInterval(id);
            }, ms);
        }, delay);
        return () => {
            clearTimeout(start);
            clearInterval(id);
        };
    }, [run, ms, delay]);
    return shown;
}

/** Slide: the page gets cut into named entries, boxes appearing one by one. */
export function PageCut({ t }) {
    const p = t.library;
    const [run, setRun] = useState(0);
    const shown = useReveal(run, 140);
    const [state, setState] = useState('ready');
    const [scan, setScan] = useState(0);
    const [scanning, setScanning] = useState(false);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        if (!scan) return undefined;
        setScanning(true);
        const id = setTimeout(() => setScanning(false), SCAN_MS);
        return () => clearTimeout(id);
    }, [scan]);

    const done = shown >= N;
    const focus = hover || (done ? DEFAULT_FOCUS : null);
    const f = focus ? TARGET[focus] : null;
    const newest = !done && shown > 0 ? TARGETS[shown - 1] : null;

    let status = `${shown} / ${N}`;
    if (done && scanning) status = p.scanning;
    else if (done && state === 'ready') status = fmt(p.reference, { n: N });
    else if (done) status = fmt(p.found, { n: N });

    const pickState = (v) => {
        setState(v);
        setScan((k) => k + 1);
    };

    return (
        <>
            <Intro eyebrow={p.eyebrow} title={p.title} sub={p.sub} />

            <Reveal className={styles.cutVisual} delay={0.1}>
                <div className={styles.frame}>
                    <HmiView state={state} label="Mock HMI with library entries">
                        {TARGETS.slice(0, shown).map((tg) => {
                            const on = focus === tg.name;
                            const dim = focus && !on && !(f && f.parent === tg.name);
                            return (
                                <motion.rect
                                    key={`${tg.name}-${run}`}
                                    x={tg.box[0]} y={tg.box[1]} width={tg.box[2]} height={tg.box[3]}
                                    rx={6}
                                    fill={on ? FILL_ON[tg.kind] : FILL[tg.kind]}
                                    stroke={COLOR[tg.kind]}
                                    strokeWidth={on ? 3 : 1.5}
                                    vectorEffect="non-scaling-stroke"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: dim ? 0.2 : 1 }}
                                    transition={{ duration: 0.3 }}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={() => setHover(tg.name)}
                                    onMouseLeave={() => setHover(null)}
                                />
                            );
                        })}
                        {scanning && (
                            <motion.rect
                                key={`scan-${scan}`}
                                y={0} height={800} width={60}
                                fill="rgba(56,189,248,0.22)"
                                initial={{ attrX: -60 }}
                                animate={{ attrX: 1280 }}
                                transition={{ duration: SCAN_MS / 1000, ease: 'easeInOut' }}
                                style={{ pointerEvents: 'none' }}
                            />
                        )}
                        {newest && <BoxLabel box={newest.box} text={newest.name} color={COLOR[newest.kind]} />}
                        {f && done && (
                            <g style={{ pointerEvents: 'none' }}>
                                <Outline box={pad(f.anchor, 2)} color={RED} dashed width={2.5} animateBox={false} />
                                <BoxLabel box={f.box} text={f.name} color={COLOR[f.kind]} />
                            </g>
                        )}
                    </HmiView>
                </div>
            </Reveal>

            <div className={styles.libControls}>
                <Segmented
                    small
                    group="state-lib"
                    label={t.stateLabel}
                    options={STATE_IDS.map((id) => ({ id, label: t.states[id] }))}
                    value={state}
                    onChange={pickState}
                />
                <span className={styles.status}>{status}</span>
                <button type="button" className={styles.textBtn} onClick={() => setRun((k) => k + 1)}>
                    <ReplayRoundedIcon sx={{ fontSize: 17 }} />
                    {p.replay}
                </button>
            </div>
            <div className={styles.legend}>
                <span><i className={styles.swatch} style={{ borderColor: BLUE }} />{p.legend.component}</span>
                <span><i className={styles.swatch} style={{ borderColor: PURPLE }} />{p.legend.item}</span>
                <span><i className={`${styles.swatch} ${styles.swatchDashed}`} style={{ borderColor: RED }} />{p.legend.anchor}</span>
            </div>
        </>
    );
}

/** Slide: the entries land in targets.db; hover a row to see its anchor. */
export function PageStore({ t }) {
    const p = t.library;
    const shown = useReveal(0, 90, 400);
    const [hover, setHover] = useState(null);
    const tableRef = useRef(null);

    useEffect(() => {
        const el = tableRef.current;
        if (el && shown < N) el.scrollTop = el.scrollHeight;
    }, [shown]);

    const done = shown >= N;
    const focus = hover || DEFAULT_FOCUS;
    const f = TARGET[focus];

    return (
        <>
            <Intro eyebrow={p.storeEyebrow} title={p.dbTitle} sub={p.dbSub} />

            <div className={styles.libBottom}>
                <Reveal className={styles.libText} delay={0.1}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={f.name}
                            className={styles.detail}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25, ease: EASE }}
                        >
                            <div className={styles.detailSample}>
                                <HmiCrop state="ready" box={pad(f.sample, 6)} label={f.name}>
                                    <Outline box={f.anchor} color={RED} dashed width={2} animateBox={false} />
                                    <Outline box={pad(f.box, 2)} color={COLOR[f.kind]} width={2} animateBox={false} />
                                </HmiCrop>
                            </div>
                            <div>
                                <code>{f.name}</code>
                                <p>{p.anchorFrom[f.anchorFrom]}</p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                    <p className={styles.hint}>{p.hint}</p>
                </Reveal>

                <Reveal className={styles.db} delay={0.2}>
                    <div className={styles.dbHead}>
                        <code>targets.db</code>
                        <span>{shown} rows</span>
                    </div>
                    <div className={styles.dbScroll} ref={tableRef}>
                        <table className={styles.dbTable}>
                            <thead>
                                <tr>{p.columns.map((col) => <th key={col}>{col}</th>)}</tr>
                            </thead>
                            <tbody>
                                {TARGETS.slice(0, shown).map((tg) => (
                                    <motion.tr
                                        key={tg.name}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                        className={done && focus === tg.name ? styles.rowOn : undefined}
                                        onMouseEnter={() => setHover(tg.name)}
                                        onMouseLeave={() => setHover(null)}
                                    >
                                        <td><code>{tg.name}</code></td>
                                        <td>
                                            <span className={styles.kindDot} style={{ background: COLOR[tg.kind] }} />
                                            {p.kinds[tg.kind]}
                                        </td>
                                        <td className={styles.mono}>{tg.box[2]}×{tg.box[3]}</td>
                                        <td className={styles.mono}>{scoreFor(tg.name, 'ready').toFixed(3)}</td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Reveal>
            </div>

            <p className={styles.note}>{p.real}</p>
        </>
    );
}
